import PropTypes from "prop-types";
import { forwardRef, useEffect } from "react";
import { Link } from "react-router-dom";

// material-ui
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// project imports
import useConfig from "hooks/useConfig";
import { useDispatch, useSelector } from "store";
import { activeItem, cleanActiveGroup, openDrawer } from "store/slices/menu";

// assets
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

import { TIENDA_HANDY } from "config";

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level }) => {
  const theme = useTheme();
  const matchesSM = useMediaQuery(theme.breakpoints.down("lg"));

  const { borderRadius } = useConfig();
  const dispatch = useDispatch();
  const { openItem } = useSelector((state) => state.menu);

  const Icon = item?.icon;
  const itemIcon = item?.icon ? (
    <Icon stroke={1.5} size="1.3rem" />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: openItem.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
        height: openItem.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
      }}
      fontSize={level > 0 ? "inherit" : "medium"}
    />
  );

  let itemTarget = "_self";
  if (item.target) {
    itemTarget = "_blank";
  }

  let listItemProps = { component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />) };
  if (item?.external) {
    listItemProps = { component: "a", href: item.url, target: itemTarget };
  }

  const itemHandler = (id, icon) => {
    dispatch(activeItem([id]));
    if (item.icon) dispatch(cleanActiveGroup());
    if (matchesSM) dispatch(openDrawer(false));
  };

  // active menu item on page load
  useEffect(() => {
    const currentIndex = document.location.pathname
      .toString()
      .split("/")
      .findIndex((id) => id === item.id);
    if (currentIndex > -1) {
      dispatch(activeItem([item.id]));
    }
    // eslint-disable-next-line
  }, []);

  return item.hidden !== true ? (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      sx={{
        borderRadius: `${borderRadius}px`,
        mb: TIENDA_HANDY ? 0 : 0.5,
        alignItems: "flex-start",
        backgroundColor: level > 1 ? "transparent !important" : "inherit",
        py: level > 1 ? 1 : 1.25,
        pl: `${level * 24}px`,
        position: "relative",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
          transform: "translateX(4px)",
          "& .MuiListItemIcon-root": {
            transform: "scale(1.1)",
          },
        },
        "&.Mui-selected": {
          backgroundColor: theme.palette.mode === "dark" ? "rgba(144, 202, 249, 0.08)" : "rgba(25, 118, 210, 0.08)",
          "&:hover": {
            backgroundColor: theme.palette.mode === "dark" ? "rgba(144, 202, 249, 0.12)" : "rgba(25, 118, 210, 0.12)",
          },
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: "50%",
          left: 0,
          transform: "translateY(-50%)",
          width: 4,
          height: openItem?.findIndex((id) => id === item.id) > -1 ? "70%" : "0%",
          backgroundColor: theme.palette.primary.main,
          borderRadius: "0 4px 4px 0",
          transition: "height 0.3s ease",
          display: TIENDA_HANDY ? "block" : "none",
        },
      }}
      selected={openItem?.findIndex((id) => id === item.id) > -1}
      onClick={() => itemHandler(item.id, item.icon)}>
      <ListItemIcon
        sx={{
          my: "auto",
          minWidth: !item?.icon ? 18 : 36,
          transition: "transform 0.2s ease",
          color: openItem?.findIndex((id) => id === item.id) > -1 ? theme.palette.primary.main : "inherit",
        }}>
        {itemIcon}
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography
            variant={openItem?.findIndex((id) => id === item.id) > -1 ? "h5" : "body1"}
            color="inherit"
            sx={{
              fontWeight: openItem?.findIndex((id) => id === item.id) > -1 ? 600 : 400,
              transition: "all 0.2s ease",
            }}>
            {item.title}
          </Typography>
        }
        secondary={
          item.caption && (
            <Typography
              variant="caption"
              sx={{
                ...theme.typography.subMenuCaption,
                opacity: 0.7,
              }}
              display="block"
              gutterBottom>
              {item.caption}
            </Typography>
          )
        }
      />

      {item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
          sx={{
            height: 24,
            borderRadius: "12px",
            fontSize: "0.75rem",
          }}
        />
      )}
    </ListItemButton>
  ) : (
    <></>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number,
};

export default NavItem;
